import { useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

/**
 * Tailwind breakpoints from tailwind.config.ts
 * These match the screens configuration in the Tailwind config
 */
const BREAKPOINTS = {
  '2xs': 384,
  xs: 512,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
  '3xl': 1728,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/**
 * Determines the current breakpoint based on screen width
 * Returns the largest breakpoint that the width satisfies
 */
const getBreakpoint = (width: number): Breakpoint => {
  if (width >= BREAKPOINTS['3xl']) return '3xl';
  if (width >= BREAKPOINTS['2xl']) return '2xl';
  if (width >= BREAKPOINTS.xl) return 'xl';
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  if (width >= BREAKPOINTS.sm) return 'sm';
  if (width >= BREAKPOINTS.xs) return 'xs';
  return '2xs';
};

/**
 * Checks if the current breakpoint is greater than or equal to the comparison breakpoint
 * @param currentBreakpoint - The current breakpoint to check
 * @param compareBreakpoint - The breakpoint to compare against
 * @returns true if current breakpoint is greater than or equal to compare breakpoint
 */
export const isGreaterThanOrEqualBreakpoint = (
  currentBreakpoint: Breakpoint,
  compareBreakpoint: Breakpoint
): boolean => {
  return BREAKPOINTS[currentBreakpoint] >= BREAKPOINTS[compareBreakpoint];
};

/**
 * Checks if the current breakpoint is greater than the comparison breakpoint
 * @param currentBreakpoint - The current breakpoint to check
 * @param compareBreakpoint - The breakpoint to compare against
 * @returns true if current breakpoint is greater than compare breakpoint
 */
export const isGreaterThanBreakpoint = (
  currentBreakpoint: Breakpoint,
  compareBreakpoint: Breakpoint
): boolean => {
  return BREAKPOINTS[currentBreakpoint] > BREAKPOINTS[compareBreakpoint];
};

/**
 * Checks if the current breakpoint is less than or equal to the comparison breakpoint
 * @param currentBreakpoint - The current breakpoint to check
 * @param compareBreakpoint - The breakpoint to compare against
 * @returns true if current breakpoint is less than or equal to compare breakpoint
 */
export const isLessThanOrEqualBreakpoint = (
  currentBreakpoint: Breakpoint,
  compareBreakpoint: Breakpoint
): boolean => {
  return BREAKPOINTS[currentBreakpoint] <= BREAKPOINTS[compareBreakpoint];
};

/**
 * Checks if the current breakpoint is less than the comparison breakpoint
 * @param currentBreakpoint - The current breakpoint to check
 * @param compareBreakpoint - The breakpoint to compare against
 * @returns true if current breakpoint is less than compare breakpoint
 */
export const isLessThanBreakpoint = (
  currentBreakpoint: Breakpoint,
  compareBreakpoint: Breakpoint
): boolean => {
  return BREAKPOINTS[currentBreakpoint] < BREAKPOINTS[compareBreakpoint];
};

type UseBreakpointResult<T extends HTMLElement> = {
  ref: React.MutableRefObject<T | null>;
  breakpoint: Breakpoint;
  isGreaterThan: (compare: Breakpoint) => boolean;
  isGreaterThanOrEqual: (compare: Breakpoint) => boolean;
  isLessThan: (compare: Breakpoint) => boolean;
  isLessThanOrEqual: (compare: Breakpoint) => boolean;
};

type UseBreakpointProps = {
  onBreakpointChange?: (bp: Breakpoint) => void;
};

/**
 * Hook that tracks screen width and returns the current Tailwind breakpoint
 * Uses debouncing to optimize performance during window resizing
 * @returns An object containing a ref, the current breakpoint value, and utility functions for breakpoint comparisons
 */
export function useBreakpoint<T extends HTMLElement = HTMLDivElement>(
  props: UseBreakpointProps = {}
): UseBreakpointResult<T> {
  const ref = useRef<T | null>(null);
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(() => {
    if (typeof window !== 'undefined') {
      return getBreakpoint(window.innerWidth);
    }
    return '2xs';
  });

  const updateBreakpoint = useDebouncedCallback(() => {
    const newBreakpoint = getBreakpoint(window.innerWidth);
    if (breakpoint === newBreakpoint) return;
    setBreakpoint((prev) => {
      if (prev !== newBreakpoint) {
        props?.onBreakpointChange?.(newBreakpoint);
        return newBreakpoint;
      }
      return prev;
    });
  }, 250);

  useEffect(() => {
    // Listen for window resize events
    window.addEventListener('resize', updateBreakpoint);

    return () => {
      window.removeEventListener('resize', updateBreakpoint);
    };
  }, [updateBreakpoint]);

  // Utility functions bound to current breakpoint
  const isGreaterThan = (compareBreakpoint: Breakpoint) =>
    isGreaterThanBreakpoint(breakpoint, compareBreakpoint);
  const isGreaterThanOrEqual = (compareBreakpoint: Breakpoint) =>
    isGreaterThanOrEqualBreakpoint(breakpoint, compareBreakpoint);
  const isLessThan = (compareBreakpoint: Breakpoint) =>
    isLessThanBreakpoint(breakpoint, compareBreakpoint);
  const isLessThanOrEqual = (compareBreakpoint: Breakpoint) =>
    isLessThanOrEqualBreakpoint(breakpoint, compareBreakpoint);

  return {
    ref,
    breakpoint,
    isGreaterThan,
    isGreaterThanOrEqual,
    isLessThan,
    isLessThanOrEqual,
  };
}
